import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, FolderOpen, Send, Trash2, Eye, EyeOff, FileText, ExternalLink, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import NombreCliente from '../../components/NombreCliente';
import SelectorAviso from '../../components/SelectorAviso';
import SelectorCliente from '../../components/SelectorCliente';
import { money, fecha, hoy, Title, Badge, Empty, ESTADO_COBRO } from './ui';

export const ESTADO_PROYECTO = {
  planificacion: { label: 'En planificación', color: '#f59e0b', bg: 'rgba(245,158,11,0.15)' },
  en_desarrollo: { label: 'En desarrollo', color: '#0ea5e9', bg: 'rgba(14,165,233,0.15)' },
  revision: { label: 'En revisión', color: '#a855f7', bg: 'rgba(168,85,247,0.15)' },
  publicacion: { label: 'En publicación', color: '#14b8a6', bg: 'rgba(20,184,166,0.15)' },
  terminado: { label: 'Terminado', color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
  cancelado: { label: 'Cancelado', color: '#ef4444', bg: 'rgba(239,68,68,0.15)' },
};

// Movimientos típicos, para cargarlos de un toque
const TIPOS_MOV = [
  { tipo: 'avance', label: 'Avance' },
  { tipo: 'minuta', label: 'Minuta de reunión' },
  { tipo: 'nota', label: 'Nota interna' },
  { tipo: 'entrega', label: 'Entrega' },
];
const ATAJOS = ['Proyecto iniciado', 'Material recibido', 'Primera versión disponible', 'Cambios solicitados', 'Nueva versión disponible', 'Proyecto finalizado'];
const PLANTILLA_MINUTA = 'Resumen de la reunión:\n\n\nTemas tratados:\n· \n\nDecisiones tomadas:\n· \n\nPróximos pasos:\n· ';

export default function AgenciaProyecto() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [form, setForm] = useState({});
  const [mov, setMov] = useState(null);
  const [guardando, setGuardando] = useState(false);
  const [avisoEstado, setAvisoEstado] = useState(null);   // pregunta al cambiar de estado
  const [clientes, setClientes] = useState([]);

  useEffect(() => { api.get('/agency/clientes').then(r => setClientes(r.data)).catch(() => {}); }, []);
  const [propuestas, setPropuestas] = useState([]);
  useEffect(() => {
    if (!form.cliente_id) return;
    api.get('/agency/propuestas').then(r => setPropuestas(r.data.filter(x => String(x.cliente_id) === String(form.cliente_id)))).catch(() => {});
  }, [form.cliente_id]);

  const cargar = useCallback(() => {
    api.get('/agency/proyectos/' + id).then(r => { setD(r.data); setForm(r.data.proyecto); }).catch(() => toast.error('No se pudo cargar'));
  }, [id]);
  useEffect(() => { cargar(); }, [cargar]);
  if (!d) return <div style={{ color: 'var(--text-muted)', padding: 40 }}>Cargando...</div>;

  const p = d.proyecto;
  const e = ESTADO_PROYECTO[form.estado] || {};
  const set = (campo) => (ev) => setForm({ ...form, [campo]: ev.target.value });

  const guardar = async (notificar = 'no', mensaje = '') => {
    setGuardando(true);
    try {
      const { data } = await api.put('/agency/proyectos/' + id, {
        cliente_id: form.cliente_id, propuesta_id: form.propuesta_id || '',
        nombre: form.nombre, estado: form.estado, fecha_inicio: (form.fecha_inicio || '').slice(0, 10) || '',
        fecha_fin: (form.fecha_fin || '').slice(0, 10) || '', resumen: form.resumen || '', drive_url: form.drive_url || '',
        visible_cliente: form.visible_cliente ? 1 : 0, notificar, mensaje,
      });
      const m = data.mudanza;
      if (m) {
        const arrastra = [m.cobros ? `${m.cobros} cobro${m.cobros === 1 ? '' : 's'}` : '', m.servicios ? `${m.servicios} servicio${m.servicios === 1 ? '' : 's'}` : ''].filter(Boolean).join(' y ');
        toast.success(`El proyecto pasó de ${m.de} a ${m.a}${arrastra ? `, con ${arrastra}` : ''}`, { duration: 7000 });
      } else {
        toast.success(notificar === 'no' ? 'Guardado'
          : `Guardado · le avisamos ${notificar === 'email' ? 'por email' : notificar === 'portal' ? 'en el portal' : 'por portal y email'}`);
      }
      setAvisoEstado(null); cargar();
    } catch { toast.error('No se pudo guardar'); } finally { setGuardando(false); }
  };

  const guardarMov = async (ev) => {
    ev.preventDefault();
    try {
      if (mov.id) await api.put('/agency/movimientos/' + mov.id, mov);
      else {
        const { data } = await api.post('/agency/movimientos', { ...mov, entidad: 'proyecto', entidad_id: id });
        if (data.aviso && data.aviso.error) toast.error(data.aviso.error);
      }
      toast.success(mov.id ? 'Actualizado' : 'Movimiento agregado');
      setMov(null); cargar();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo guardar'); }
  };

  const enviarMov = async (m) => {
    if (!window.confirm(`Enviarle "${m.titulo}" por mail al cliente?`)) return;
    try {
      const { data } = await api.post(`/agency/movimientos/${m.id}/enviar`);
      toast.success('Enviado a ' + data.destinos.join(', '), { duration: 6000 });
      cargar();
    } catch (err) { toast.error(err.response?.data?.error || 'No se pudo enviar'); }
  };

  const borrarMov = async (m) => {
    if (!window.confirm('¿Borrar este movimiento?')) return;
    try { await api.delete('/agency/movimientos/' + m.id); cargar(); } catch { toast.error('Error'); }
  };

  const verClienteMov = async (m) => {
    try { await api.put('/agency/movimientos/' + m.id, { visible_cliente: m.visible_cliente ? 0 : 1 }); cargar(); }
    catch { toast.error('Error'); }
  };

  const card = { marginBottom: 18 };
  const td = { padding: '9px 8px', borderTop: '1px solid var(--border)', fontSize: 13 };

  return (
    <div>
      <button className="btn btn-ghost btn-sm" onClick={() => navigate('/agencia/clientes/' + p.cliente_id)} style={{ marginBottom: 12 }}>
        <ArrowLeft size={15} /> <NombreCliente nombre={p.cliente} logo={p.logo_url} size={18} />
      </button>

      <Title seccion={p.cliente} right={<Badge label={e.label} color={e.color} bg={e.bg} />}>{p.nombre}</Title>

      <div className="card" style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12 }}>
          <div className="form-group">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={13} /> Cliente</label>
            <SelectorCliente clientes={clientes} clienteId={form.cliente_id} permitirSinCliente={false} permitirNuevo={false}
              onCambio={({ cliente_id }) => setForm({ ...form, cliente_id })} />
            {!form.cliente_id ? (
              <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 5 }}>
                Elegí un cliente: un proyecto no puede quedar sin dueño.
              </div>
            ) : String(form.cliente_id) !== String(p.cliente_id) ? (
              <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: 5 }}>
                Se muda a este cliente y se lleva sus cobros y servicios.
              </div>
            ) : null}
          </div>
          <div className="form-group"><label>Nombre</label><input value={form.nombre || ''} onChange={set('nombre')} /></div>
          <div className="form-group"><label>Estado</label>
            <select value={form.estado || 'planificacion'} onChange={set('estado')}>
              {Object.entries(ESTADO_PROYECTO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>Al guardar un cambio de estado te preguntamos si querés avisarle al cliente.</div>
          </div>
          <div className="form-group"><label>Inicio</label><input type="date" value={(form.fecha_inicio || '').slice(0, 10)} onChange={set('fecha_inicio')} /></div>
          <div className="form-group"><label>Entrega</label><input type="date" value={(form.fecha_fin || '').slice(0, 10)} onChange={set('fecha_fin')} /></div>
        </div>
        {/* La propuesta que dio origen al trabajo. Al vincularla queda
            confirmada y el cliente la ve en su portal, adentro del proyecto. */}
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><FileText size={13} /> Propuesta de origen</label>
          <select value={form.propuesta_id || ''} onChange={set('propuesta_id')}>
            <option value="">Sin propuesta</option>
            {propuestas.map(x => (
              <option key={x.id} value={x.id}>{x.titulo}{x.pdf_url ? '' : ' (sin PDF)'}</option>
            ))}
          </select>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {form.propuesta_id
              ? 'Al guardar queda confirmada y el cliente puede abrir el PDF desde su portal.'
              : propuestas.length ? 'Elegí cuál fue, si este trabajo salió de una propuesta.' : 'Este cliente no tiene propuestas cargadas.'}
          </div>
        </div>
        <div className="form-group"><label>Carpeta de Google Drive</label>
          <input value={form.drive_url || ''} onChange={set('drive_url')} placeholder="https://drive.google.com/drive/folders/..." />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>El cliente la abre con un botón desde su portal. Acordate de compartirle la carpeta desde Drive.</div>
        </div>
        <div className="form-group"><label>Resumen</label>
          <textarea rows={2} value={form.resumen || ''} onChange={set('resumen')} style={{ fontFamily: 'inherit', fontSize: 13 }} /></div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.visible_cliente} onChange={ev => setForm({ ...form, visible_cliente: ev.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
          Visible en el portal del cliente
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-primary" disabled={guardando || !form.cliente_id}
            onClick={() => (form.estado !== p.estado ? setAvisoEstado({ canal: 'portal', mensaje: '' }) : guardar())}>
            <Save size={16} /> {guardando ? 'Guardando...' : 'Guardar'}</button>
          {form.drive_url && <a className="btn btn-secondary" href={form.drive_url} target="_blank" rel="noopener noreferrer"><FolderOpen size={16} /> Abrir Drive</a>}
          {d.propuesta && d.propuesta.pdf_url && (
            <a className="btn btn-secondary" href={d.propuesta.pdf_url} target="_blank" rel="noopener noreferrer"><FileText size={16} /> Propuesta</a>
          )}
        </div>
      </div>

      {/* Historial */}
      <div className="card" style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, gap: 10, flexWrap: 'wrap' }}>
          <h3 style={{ fontSize: 16, fontWeight: 700 }}>Historial del proyecto</h3>
          <button className="btn btn-secondary btn-sm" onClick={() => setMov({ tipo: 'avance', titulo: '', detalle: '', fecha: hoy(), visible_cliente: 1 })}>
            <Plus size={14} /> Agregar movimiento
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>Todo lo que marques como visible lo ve el cliente en su portal.</div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {ATAJOS.map(a => (
            <button key={a} className="btn btn-ghost btn-sm" style={{ fontSize: 12, border: '1px solid var(--border)' }}
              onClick={() => setMov({ tipo: 'avance', titulo: a, detalle: '', fecha: hoy(), visible_cliente: 1 })}>{a}</button>
          ))}
        </div>

        {d.movimientos.length ? d.movimientos.map(m => (
          <div key={m.id} style={{ borderTop: '1px solid var(--border)', padding: '12px 0', opacity: m.visible_cliente ? 1 : 0.6 }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fecha(m.fecha)}</span>
              <span style={{ fontWeight: 600, fontSize: 14 }}>{m.titulo}</span>
              {m.tipo !== 'avance' && <Badge label={(TIPOS_MOV.find(t => t.tipo === m.tipo) || {}).label || m.tipo} />}
              {m.enviado_at && <span style={{ fontSize: 11, color: '#22c55e' }}>enviado</span>}
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 2 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => verClienteMov(m)} title={m.visible_cliente ? 'Ocultar al cliente' : 'Mostrar al cliente'}>
                  {m.visible_cliente ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setMov({ ...m, fecha: (m.fecha || '').slice(0, 10) })} title="Editar"><FileText size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => enviarMov(m)} title="Enviar por mail"><Send size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => borrarMov(m)} title="Borrar"><Trash2 size={14} /></button>
              </div>
            </div>
            {m.detalle && <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-line', marginTop: 6, lineHeight: 1.6 }}>{m.detalle}</div>}
            {m.archivo_url && <a href={m.archivo_url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12 }}><ExternalLink size={12} /> Ver enlace</a>}
          </div>
        )) : <Empty>Todavía no cargaste movimientos</Empty>}
      </div>

      {/* Pagos y servicios */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 18 }}>
        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Pagos del proyecto</h3>
          {d.cobros.length ? (
            <div className="table-container" style={{ border: 0, borderRadius: 0 }}><table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>{d.cobros.map(c => {
                const ec = ESTADO_COBRO[c.estado] || {};
                return (
                  <tr key={c.id} style={{ opacity: c.habilitado ? 1 : 0.6 }}>
                    <td style={td}>{c.concepto}
                      {!c.habilitado && c.estado !== 'anulado' && <div><Badge label="A pasar" color="#64748b" bg="rgba(100,116,139,0.15)" /></div>}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap', fontSize: 12, color: 'var(--text-muted)' }}>{fecha(c.vencimiento)}</td>
                    <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{money(c.monto)}</td>
                    <td style={td}><Badge label={ec.label} color={ec.color} bg={ec.bg} /></td>
                  </tr>
                );
              })}</tbody>
            </table></div>
          ) : <Empty>Sin pagos asociados</Empty>}
        </div>

        <div className="card">
          <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Servicios del proyecto</h3>
          {d.servicios.length ? d.servicios.map(s => (
            <div key={s.id} style={{ borderTop: '1px solid var(--border)', padding: '10px 0', display: 'flex', justifyContent: 'space-between', gap: 10, cursor: 'pointer' }}
              onClick={() => navigate('/agencia/servicios/' + s.id)}>
              <div><div style={{ fontWeight: 600, fontSize: 13 }}>{s.nombre}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {s.tipo === 'abono' ? 'Abono mensual' : s.tipo === 'anual' ? `Renovación anual · vence ${fecha(s.proximo_vencimiento)}` : 'Pago único'}
                </div></div>
              <div style={{ fontWeight: 600, fontSize: 13, whiteSpace: 'nowrap' }}>{money(s.monto)}</div>
            </div>
          )) : <Empty>Sin servicios asociados</Empty>}
        </div>
      </div>

      {avisoEstado && (
        <div className="modal-overlay" onClick={() => setAvisoEstado(null)}>
          <div className="modal" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 460 }}>
            <h2>El proyecto pasa a {(ESTADO_PROYECTO[form.estado] || {}).label}</h2>
            <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 16 }}>¿Querés que el cliente se entere?</p>
            <SelectorAviso value={avisoEstado.canal} onChange={c => setAvisoEstado({ ...avisoEstado, canal: c })} />
            {avisoEstado.canal !== 'no' && (
              <div className="form-group"><label>Mensaje (opcional)</label>
                <textarea rows={3} value={avisoEstado.mensaje} onChange={ev => setAvisoEstado({ ...avisoEstado, mensaje: ev.target.value })}
                  style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder="Ya arrancamos con el desarrollo, te vamos contando." /></div>
            )}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setAvisoEstado(null)}>Cancelar</button>
              <button className="btn btn-primary" onClick={() => guardar(avisoEstado.canal, avisoEstado.mensaje)} disabled={guardando}>Guardar</button>
            </div>
          </div>
        </div>
      )}

      {mov && (
        <div className="modal-overlay" onClick={() => setMov(null)}>
          <div className="modal" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 520 }}>
            <h2>{mov.id ? 'Editar movimiento' : 'Nuevo movimiento'}</h2>
            <form onSubmit={guardarMov}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group"><label>Tipo</label>
                  <select value={mov.tipo} onChange={ev => setMov({ ...mov, tipo: ev.target.value, detalle: ev.target.value === 'minuta' && !mov.detalle ? PLANTILLA_MINUTA : mov.detalle })}>
                    {TIPOS_MOV.map(t => <option key={t.tipo} value={t.tipo}>{t.label}</option>)}
                  </select></div>
                <div className="form-group"><label>Fecha</label>
                  <input type="date" value={mov.fecha || hoy()} onChange={ev => setMov({ ...mov, fecha: ev.target.value })} /></div>
              </div>
              <div className="form-group"><label>Título</label>
                <input value={mov.titulo} onChange={ev => setMov({ ...mov, titulo: ev.target.value })} required autoFocus placeholder="Primera versión disponible" /></div>
              <div className="form-group"><label>Detalle</label>
                <textarea rows={mov.tipo === 'minuta' ? 10 : 4} value={mov.detalle || ''} onChange={ev => setMov({ ...mov, detalle: ev.target.value })}
                  style={{ fontFamily: 'inherit', fontSize: 13 }} placeholder={mov.tipo === 'minuta' ? PLANTILLA_MINUTA : 'Lo que quieras contar'} /></div>
              <div className="form-group"><label>Enlace (Drive, versión de prueba, reporte)</label>
                <input value={mov.archivo_url || ''} onChange={ev => setMov({ ...mov, archivo_url: ev.target.value })} placeholder="https://" /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={mov.visible_cliente !== 0} onChange={ev => setMov({ ...mov, visible_cliente: ev.target.checked ? 1 : 0 })} style={{ width: 16, height: 16 }} />
                Que lo vea el cliente en su portal
              </label>
              {mov.visible_cliente !== 0 && !mov.id && (
                <SelectorAviso value={mov.notificar || 'portal'} onChange={c => setMov({ ...mov, notificar: c })} />
              )}
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setMov(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
